import { ChangeEvent, ElementType, ReactNode, useState } from "react";

import { makeStyles, createStyles, Theme, Box, SvgIcon, TextField } from "@material-ui/core";
import AutoComplete from "@material-ui/lab/Autocomplete";

import { InputWrapper, InputWrapperProps } from "~components/InputWrapper";
import { Arrow } from "~components/Icons";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    select: {
      // hack to remove underline
      "& .MuiInput-underline:before": {
        borderBottom: "none",
      },
      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
        borderBottom: "none",
      },
      "& .MuiInput-underline:after": {
        borderBottom: "none",
      },

      display: "flex",
      justifyContent: "space-between",
      padding: `${theme.spacing(1.5)}px ${theme.spacing(2)}px`,

      fontSize: 16,
      lineHeight: "normal",
      color: theme.palette.dark.main,

      backgroundColor: "white",
      borderRadius: 4,
      boxShadow: "0 1px 1px 0 rgba(51, 91, 130, 0.12), 0 0 1px 0 rgba(10, 31, 68, 0.1)",

      transition: "box-shadow ease-in-out .18s",

      "&:focus-within": {
        backgroundColor: "white",
        borderRadius: 4,
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}, 0 4px 8px 0 rgba(26, 99, 171, 0.12), 0 0 1px 0 rgba(26, 99, 171, 0.1)`,
      },

      "&:disabled": {
        opacity: 0.35,
      },
    },
    listbox: {
      padding: `${theme.spacing(1)}px ${theme.spacing(1)}px`,
    },
    listboxPaper: {
      borderRadius: 4,
      boxShadow: "0 10px 12px 0 rgba(51, 91, 130, 0.12), 0 0 1px 0 rgba(10, 31, 68, 0.1)",
    },
    popupIndicator: {
      "& svg": {
        fontSize: "24px",
      },

      color: theme.palette.primary.main,

      transform: `rotate(180deg)`,
      transition: "transform .18s ease",
    },
    popupIndicatorOpen: {
      transform: `rotate(0deg)`,
    },
    option: {
      padding: `${theme.spacing(1.2)}px ${theme.spacing(1)}px`,
      borderRadius: 2,
    },
    input: {
      margin: 0,
      padding: "0 !important",
    },
  }),
);

type Item = {
  label: string;
  value: number | string;
  icon?: ReactNode;
};

type InputProps = {
  items: Item[];
  placeholder?: string;
  onChange?: (value: Item | null) => void;
} & Omit<InputWrapperProps, "children">;

export const Select = ({ label, error, id, items, placeholder, onChange }: InputProps) => {
  const [icon, setIcon] = useState<ReactNode>();
  const customId = `select-${id ?? label}`;
  const classes = useStyles();

  const handleChange = (_: ChangeEvent<{}>, value: Item | null) => {
    if (value === null && icon) {
      setIcon(undefined);
    }

    if (value?.icon) {
      setIcon(value.value);
    }

    if (onChange) {
      onChange(value);
    }
  };

  return (
    <InputWrapper label={label} error={error} id={customId}>
      <AutoComplete
        id={customId}
        options={items}
        onChange={handleChange}
        popupIcon={<SvgIcon component={Arrow} />}
        getOptionLabel={(option) => option.label}
        classes={{
          listbox: classes.listbox,
          option: classes.option,
          input: classes.input,
          paper: classes.listboxPaper,
          popupIndicator: classes.popupIndicator,
          popupIndicatorOpen: classes.popupIndicatorOpen,
        }}
        renderOption={(option) => (
          <Box display="flex" alignItems="center">
            {option.icon && (
              <SvgIcon
                component={option.icon as ElementType<any>}
                style={{ fontSize: 16, marginRight: 12 }}
              />
            )}
            {option.label}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            className={classes.select}
            ref={params.InputProps.ref}
            inputProps={{
              ...params.inputProps,
              placeholder,
              disableUnderline: true,
              autoComplete: false,
            }}
            {...(icon && {
              InputProps: {
                ...params.InputProps,
                startAdornment: (
                  <SvgIcon
                    component={items?.find((item) => item.value === icon)?.icon as ElementType<any>}
                    style={{ fontSize: 16, marginRight: 12 }}
                  />
                ),
              },
            })}
          />
        )}
      />
    </InputWrapper>
  );
};
